/* ==========================================================================
   COSMIC 9 GEMS — HEADER
   ==========================================================================
   Adds only the two behaviours Dawn does not already provide:

     1. Pointer hover intent on the desktop navigation panels. Dawn's
        <header-menu> is click-only; a premium desktop header opens on hover,
        but only on real pointer devices and only after a short delay so the
        panel does not flash while the cursor crosses the bar.

   The panels used to also need viewport-edge collision handling, because
   each was anchored to its own trigger. They now span the whole navigation
   row (see .c9-mega in c9-header.css), so there is no edge left to collide
   with and that code — and the --c9-mega-offset property it wrote — is gone
   rather than kept as a no-op.

   Everything else — focus trapping, Escape, scroll locking, the closing
   animation, sticky hide/reveal, search modal, predictive search — is Dawn's
   and is untouched.
   ========================================================================== */

(function () {
  'use strict';

  var OPEN_DELAY = 120;
  var CLOSE_DELAY = 200;

  var finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
  var desktop = window.matchMedia('(min-width: 990px)');

  function C9Nav(nav) {
    this.nav = nav;
    this.items = Array.prototype.slice.call(nav.querySelectorAll('.c9-nav__item'));
    this.openTimer = null;
    this.closeTimer = null;
    this.init();
  }

  C9Nav.prototype.init = function () {
    var self = this;

    this.items.forEach(function (item) {
      var details = item.querySelector('details');
      if (!details) return;

      var summary = details.querySelector('summary');
      var panel = details.querySelector('.c9-mega');
      if (!summary || !panel) return;

      item.addEventListener('mouseenter', function () {
        if (!self.canHover()) return;
        self.scheduleOpen(details);
      });

      item.addEventListener('mouseleave', function () {
        if (!self.canHover()) return;
        self.scheduleClose(details);
      });

      // Keyboard: Escape closes and returns focus to the trigger.
      details.addEventListener('keydown', function (event) {
        if (event.key !== 'Escape') return;
        if (!details.open) return;
        event.stopPropagation();
        self.close(details);
        summary.focus();
      });
    });

    // A click anywhere outside the nav closes any open panel. Dawn's
    // DetailsDisclosure only closes on focusout, which a mouse click does
    // not always produce.
    document.addEventListener('click', function (event) {
      if (self.nav.contains(event.target)) return;
      self.closeAll();
    });

    // Escape from anywhere. The per-details handler above only fires while
    // focus is inside the panel, and a panel opened by hover holds no focus
    // at all -- so without this, a hovered-open panel could not be dismissed
    // from the keyboard.
    document.addEventListener('keydown', function (event) {
      if (event.key !== 'Escape') return;
      if (!self.hasOpen()) return;
      self.closeAll();
    });

    window.addEventListener(
      'resize',
      function () {
        self.closeAll();
      },
      { passive: true }
    );
  };

  C9Nav.prototype.hasOpen = function () {
    return this.items.some(function (item) {
      var details = item.querySelector('details');
      return !!details && details.open;
    });
  };

  C9Nav.prototype.canHover = function () {
    return finePointer.matches && desktop.matches;
  };

  C9Nav.prototype.scheduleOpen = function (details) {
    var self = this;
    window.clearTimeout(this.closeTimer);
    window.clearTimeout(this.openTimer);

    this.openTimer = window.setTimeout(function () {
      self.closeAll(details);
      if (!details.open) {
        details.open = true;
        var summary = details.querySelector('summary');
        if (summary) summary.setAttribute('aria-expanded', 'true');
      }
    }, OPEN_DELAY);
  };

  C9Nav.prototype.scheduleClose = function (details) {
    var self = this;
    window.clearTimeout(this.openTimer);
    window.clearTimeout(this.closeTimer);

    this.closeTimer = window.setTimeout(function () {
      self.close(details);
    }, CLOSE_DELAY);
  };

  C9Nav.prototype.close = function (details) {
    if (!details.open) return;
    // Keep focus out of a panel that is about to be hidden.
    if (details.contains(document.activeElement)) {
      var summary = details.querySelector('summary');
      if (summary) summary.focus();
    }
    details.open = false;
    var trigger = details.querySelector('summary');
    if (trigger) trigger.setAttribute('aria-expanded', 'false');
  };

  C9Nav.prototype.closeAll = function (except) {
    var self = this;
    this.items.forEach(function (item) {
      var details = item.querySelector('details');
      if (details && details !== except) self.close(details);
    });
  };

  function init() {
    document.querySelectorAll('.c9-nav').forEach(function (nav) {
      if (nav.dataset.c9NavReady === 'true') return;
      nav.dataset.c9NavReady = 'true';
      new C9Nav(nav);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  // Theme editor re-renders the header section without a page load.
  document.addEventListener('shopify:section:load', function (event) {
    if (event.target && event.target.querySelector('.c9-nav')) init();
  });

  /* ------------------------------------------------------------------------
     ANNOUNCEMENT ROTATOR

     A cross fade over a fixed-height track. The items are stacked, not laid
     out in a row, so the bar's height is the tallest item's height at all
     times and rotation never moves the page beneath it — which is the whole
     reason this is not a carousel: a scroll-snap track would reflow the
     header on every advance.

     Lives here rather than in its own asset because it is ~70 lines and the
     header already ships one script.
     --------------------------------------------------------------------- */

  function C9Announcement(el) {
    this.el = el;
    this.items = Array.prototype.slice.call(
      el.querySelectorAll('[data-c9-announce-item]')
    );
    if (this.items.length < 2) return;

    this.status = el.querySelector('[data-c9-announce-status]');
    this.index = 0;
    this.timer = null;

    var interval = parseFloat(el.getAttribute('data-interval') || '0');
    this.delay = interval > 0 ? interval * 1000 : 0;

    var reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (reduced.matches) this.delay = 0;

    var self = this;

    var prev = el.querySelector('[data-c9-announce-prev]');
    var next = el.querySelector('[data-c9-announce-next]');
    if (prev) {
      prev.addEventListener('click', function () {
        self.stop();
        self.go(self.index - 1);
      });
    }
    if (next) {
      next.addEventListener('click', function () {
        self.stop();
        self.go(self.index + 1);
      });
    }

    // Any intent to read pauses the rotation, and leaving resumes it.
    el.addEventListener('pointerenter', function () {
      self.stop();
    });
    el.addEventListener('pointerleave', function () {
      self.start();
    });
    el.addEventListener('focusin', function () {
      self.stop();
    });
    el.addEventListener('focusout', function () {
      if (!el.contains(document.activeElement)) self.start();
    });

    document.addEventListener('visibilitychange', function () {
      if (document.hidden) {
        self.stop();
      } else {
        self.start();
      }
    });

    this.start();
  }

  C9Announcement.prototype.go = function (next) {
    var total = this.items.length;
    var target = ((next % total) + total) % total;
    if (target === this.index) return;

    var current = this.items[this.index];
    var incoming = this.items[target];

    current.classList.remove('is-active');
    current.setAttribute('aria-hidden', 'true');

    incoming.classList.add('is-active');
    incoming.removeAttribute('aria-hidden');

    this.index = target;

    // Announced only on a deliberate change, never on the initial render.
    if (this.status) this.status.textContent = incoming.textContent.trim();
  };

  C9Announcement.prototype.start = function () {
    if (!this.delay) return;
    this.stop();
    var self = this;
    this.timer = window.setInterval(function () {
      self.go(self.index + 1);
    }, this.delay);
  };

  C9Announcement.prototype.stop = function () {
    if (!this.timer) return;
    window.clearInterval(this.timer);
    this.timer = null;
  };

  if (!window.customElements.get('c9-announcement')) {
    window.customElements.define(
      'c9-announcement',
      class extends HTMLElement {
        connectedCallback() {
          if (this.dataset.c9Ready === 'true') return;
          this.dataset.c9Ready = 'true';
          // Retained by its own listeners; nothing else needs the instance.
          new C9Announcement(this);
        }
      }
    );
  }
})();
